// src/client/game/ui/Hud.ts

import Phaser from "phaser";
import { GridLayout } from "../render/GridLayout";

export class Hud {
  private levelText?: Phaser.GameObjects.Text;
  private progressBg?: Phaser.GameObjects.Rectangle;
  private progressFill?: Phaser.GameObjects.Rectangle;

  private statsText?: Phaser.GameObjects.Text;
  private statusText?: Phaser.GameObjects.Text;

  private score = 0;
  private timeMs = 0;
  private status = "";

  private levelId: number | null = null;
  private levelLabel: string | null = null;
  private levelScore = 0;
  private levelTarget = 0;

  private progressWidth = 0;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly layout: GridLayout
  ) {}

  createStaticUI(): void {
    const fontFamily = "monospace";

    // Create objects once; positions/sizes are handled by relayout().
    this.levelText = this.scene.add
      .text(0, 0, "", {
        fontFamily,
        fontSize: "13px",
        color: "#dcd4d4",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(1000);

    this.progressBg = this.scene.add
      .rectangle(0, 0, 10, 8, 0x222222)
      .setOrigin(0.5)
      .setDepth(999);

    // Fill anchored to the left edge of the bg bar
    this.progressFill = this.scene.add
      .rectangle(0, 0, 10, 8, 0x7bd389)
      .setOrigin(0, 0.5)
      .setDepth(1000);

    this.statsText = this.scene.add
      .text(0, 0, "", {
        fontFamily,
        fontSize: "16px",
        color: "#dcd4d4",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(1000);

    this.statusText = this.scene.add
      .text(0, 0, "", {
        fontFamily,
        fontSize: "11px",
        color: "#888888",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(1000);

    this.relayout();
  }

  /** Reposition HUD so it's always *below* the playfield (not over the grid). */
  relayout(): void {
    const { width, height } = this.scene.scale;
    const centerX = width / 2;

    const hudTop = this.layout.getHudTopY();
    const reserve = (this.layout as any).getHudReservePx?.() ?? 120;

    // Put stats line comfortably inside the reserved HUD strip.
    const desiredCenterY = hudTop + Math.max(34, Math.min(60, reserve * 0.45));
    const centerY = Math.min(desiredCenterY, height - 44);

    // Level label + progress bar live above stats.
    const levelY = centerY - 38;
    const barY = centerY - 20;

    const barH = 8;
    const barW = Math.max(180, Math.min(360, Math.floor(width * 0.62)));
    this.progressWidth = barW;

    this.levelText?.setPosition(centerX, levelY);

    this.progressBg
      ?.setPosition(centerX, barY)
      .setSize(barW, barH)
      .setDisplaySize(barW, barH);

    this.progressFill
      ?.setPosition(centerX - barW / 2, barY)
      .setSize(barW, barH)
      .setDisplaySize(barW, barH);

    this.statsText?.setPosition(centerX, centerY);
    this.statusText?.setPosition(centerX, centerY + 22);

    this.refresh();
  }

  private refresh(): void {
    if (!this.statsText || !this.statusText) return;

    const seconds = this.timeMs / 1000;
    const timeStr = `${seconds.toFixed(1)}s`;

    this.statsText.setText(`Score: ${this.score}   Time: ${timeStr}`);
    this.statusText.setText(this.status);

    const hasLevel = this.levelId !== null;

    this.levelText?.setVisible(hasLevel);
    this.progressBg?.setVisible(hasLevel);
    this.progressFill?.setVisible(hasLevel);

    if (!hasLevel || !this.levelText || !this.progressFill) return;

    const label = this.levelLabel ? ` — ${this.levelLabel}` : "";
    const score = Math.max(0, Math.floor(this.levelScore));
    const target = Math.max(1, Math.floor(this.levelTarget));
    this.levelText.setText(`Lvl ${this.levelId}${label}   ${score}/${target}`);

    const pct = Math.max(0, Math.min(1, score / target));
    const w = Math.max(2, Math.floor(this.progressWidth * pct));
    this.progressFill.setDisplaySize(w, this.progressFill.height);
  }

  setScore(score: number): void {
    this.score = score;
    this.refresh();
  }

  setMeta(timeMs: number, _laserLevel: number): void {
    this.timeMs = timeMs;
    this.refresh();
  }

  setLevelProgress(
    levelId: number,
    levelLabel: string,
    levelScore: number,
    levelTarget: number
  ): void {
    this.levelId = levelId;
    this.levelLabel = levelLabel;
    this.levelScore = Math.max(0, Math.floor(levelScore));
    this.levelTarget = Math.max(0, Math.floor(levelTarget));
    this.refresh();
  }

  clearLevelProgress(): void {
    this.levelId = null;
    this.levelLabel = null;
    this.levelScore = 0;
    this.levelTarget = 0;
    this.refresh();
  }

  setStatus(message: string): void {
    this.status = message;
    this.refresh();
  }
}
